import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  AlertTriangle, 
  Heart, 
  Flame, 
  Users, 
  HelpCircle,
  MapPin,
  X,
  Volume2,
  VolumeX,
  Clock,
  Radio,
  Navigation,
  UserCheck,
  Shield,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { AcknowledgePanel } from './AcknowledgePanel';
import { EmergencyChat } from './EmergencyChat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Alert category configurations - MORE VIBRANT COLORS
const ALERT_CONFIG = {
  threat: { 
    color: '#ff0000',  // Pure red - more vibrant
    bgColor: 'rgba(255, 0, 0, 0.25)',
    borderColor: '#ff3333',
    icon: AlertTriangle, 
    label: 'THREAT',
    soundFreq: 800
  },
  medical: { 
    color: '#00bfff',  // Deep sky blue - more vibrant
    bgColor: 'rgba(0, 191, 255, 0.25)',
    borderColor: '#33ccff',
    icon: Heart, 
    label: 'MEDICAL',
    soundFreq: 600
  },
  disturbance: { 
    color: '#ff6600',  // Bright orange
    bgColor: 'rgba(255, 102, 0, 0.25)',
    borderColor: '#ff8533',
    icon: Users, 
    label: 'DISTURBANCE',
    soundFreq: 500
  },
  fire: { 
    color: '#ffcc00',  // Bright yellow
    bgColor: 'rgba(255, 204, 0, 0.25)',
    borderColor: '#ffd633',
    icon: Flame, 
    label: 'FIRE',
    soundFreq: 700
  },
  child_safety: { 
    color: '#a855f7',  // Purple for child safety
    bgColor: 'rgba(168, 85, 247, 0.25)',
    borderColor: '#c084fc',
    icon: Shield, 
    label: 'CHILD SAFETY',
    soundFreq: 750
  },
  general: { 
    color: '#cccccc',  // Brighter gray
    bgColor: 'rgba(204, 204, 204, 0.2)',
    borderColor: '#dddddd',
    icon: HelpCircle, 
    label: 'GENERAL',
    soundFreq: 400
  },
};

const GlobalAlertOverlay = () => {
  const { alerts, setAlerts, user, messages, setMessages, sendWsMessage, isSecurityHead, wsConnected } = useAuth();
  const navigate = useNavigate();
  const [activeAlert, setActiveAlert] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [chatExpanded, setChatExpanded] = useState(true); // Auto-expand chat
  const [newMessage, setNewMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [selectedEta, setSelectedEta] = useState('on_site');
  const [acking, setAcking] = useState(false);
  // iOS Safari does not implement navigator.vibrate. On iPhones we fall back
  // to an audio siren — that's what users physically feel through speaker +
  // Taptic Engine when the phone is in a pocket. Android stays silent and
  // uses real vibration (per user's earlier request).
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(navigator.userAgent || '');
  const [audioMuted, setAudioMuted] = useState(() => localStorage.getItem('alertAudioMuted') === 'true');

  const vibrationIntervalRef = useRef(null);
  const vibrationTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainRef = useRef(null);
  const pulseIntervalRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatInputRef = useRef(null);
  const overlayRef = useRef(null); // For preventing scroll propagation

  // Auto-scroll chat to bottom when messages change - MORE AGGRESSIVE
  useEffect(() => {
    if (chatExpanded && chatEndRef.current) {
      // Immediate scroll for new messages
      chatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      // Also smooth scroll after a short delay for visual feedback
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50);
    }
  }, [messages, messages.length, chatExpanded]);

  // Scroll to bottom when chat expands
  useEffect(() => {
    if (chatExpanded) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        chatInputRef.current?.focus();
      }, 100);
    }
  }, [chatExpanded]);

  // Voice-to-text functions for emergency chat - IMPROVED
  const startVoiceRecording = async () => {
    try {
      // Request high quality audio for better transcription
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Optimal for speech recognition
          channelCount: 1
        } 
      });
      
      // Use better codec if available
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      
      const recorder = new MediaRecorder(stream, { 
        mimeType,
        audioBitsPerSecond: 128000 // Higher bitrate for quality
      });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        // Only process if we have actual audio data
        if (blob.size < 1000) {
          toast.error('Recording too short. Hold longer.');
          return;
        }
        
        toast.info('Processing speech...'); // Show processing indicator
        
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        
        try {
          const response = await axios.post(`${API_URL}/voice/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000 // 30 second timeout
          });
          if (response.data.text && response.data.text.trim()) {
            setNewMessage(prev => prev + (prev ? ' ' : '') + response.data.text.trim());
            toast.success('Speech recognized!');
          } else {
            toast.error('No speech detected. Try again.');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error('Transcription failed. Try again.');
        }
      };

      // Start recording with timeslice for faster processing
      recorder.start(100);
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Microphone access denied');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  // Find the most recent active alert that hasn't been dismissed
  useEffect(() => {
    const activeAlerts = alerts.filter(a => 
      a.status === 'active' && !dismissed.has(a.id)
    );
    
    if (activeAlerts.length > 0) {
      const sortedAlerts = [...activeAlerts].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setActiveAlert(sortedAlerts[0]);
    } else {
      setActiveAlert(null);
    }
  }, [alerts, dismissed]);

  // Trigger vibration when an active alert exists. Audio sirens are DISABLED —
  // the alert relies on vibration + on-screen pulse only.
  useEffect(() => {
    if (activeAlert) {
      startVibration(activeAlert.category, activeAlert.severity);
      // iOS-only audio fallback (since Apple refuses to implement navigator.vibrate).
      // Emergency only — non-emergency stays silent on iPhone too.
      if (isIOS && !audioMuted && activeAlert.severity !== 'non_emergency') {
        playAlertSound(activeAlert.category);
      }
    } else {
      stopVibration();
      stopAlertSound();
    }

    return () => {
      stopVibration();
      stopAlertSound();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAlert, audioMuted]);

  // iOS-only audio siren. Apple deliberately omits navigator.vibrate from
  // Safari, so on iPhones we play a loud pulsing square-wave through the
  // phone's speaker. The speaker membrane + iOS notification haptic engine
  // produce a perceived vibration when the phone is in a pocket. Android
  // does NOT trigger this — Android gets the silent + violent vibration
  // path per the user's spec.
  const playAlertSound = (category) => {
    try {
      if (!audioContextRef.current) {
        // eslint-disable-next-line no-undef
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return; // Older browser without WebAudio — give up gracefully
        audioContextRef.current = new AC();
      }
      const ctx = audioContextRef.current;
      // Resume context (iOS suspends it after page load until a user gesture).
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      stopAlertSound();

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.type = 'square';
      gain.gain.value = 0.15; // Loud but not painful through phone speakers

      // Per-category base frequency so different alert types are
      // distinguishable by ear.
      const baseFreq = {
        threat: 880,        // A5
        medical: 740,       // F#5
        fire: 1000,         // ~C6
        disturbance: 660,   // E5
        child_safety: 950,
        general: 700,
      }[category] || 700;

      oscillatorRef.current = oscillator;
      gainRef.current = gain;
      oscillator.start();

      let high = true;
      pulseIntervalRef.current = setInterval(() => {
        if (oscillatorRef.current) {
          oscillatorRef.current.frequency.value = high ? baseFreq : baseFreq * 0.75;
          high = !high;
        }
      }, 350);
    } catch (err) {
      console.warn('iOS audio siren failed:', err);
    }
  };

  const stopAlertSound = () => {
    if (pulseIntervalRef.current) {
      clearInterval(pulseIntervalRef.current);
      pulseIntervalRef.current = null;
    }
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch (e) { /* already stopped — safe */ console.debug('[overlay] osc already stopped'); }
      oscillatorRef.current = null;
    }
  };

  const toggleAudioMute = (e) => {
    e?.stopPropagation();
    const next = !audioMuted;
    setAudioMuted(next);
    localStorage.setItem('alertAudioMuted', String(next));
    if (next) stopAlertSound();
  };

  // MAXIMUM-INTENSITY ("violent") vibration patterns.
  // Strategy: long sustained pulses (~900-1000 ms) with the smallest possible
  // gaps the OS will honour (30-50 ms) so the user perceives a continuous,
  // jackhammer-style buzz rather than discrete taps. The pattern loops with a
  // 30 ms gap between iterations until the alert is dismissed/resolved.
  // Note: iOS Safari does not implement navigator.vibrate — there is no
  // browser-level workaround for that limitation.
  const startVibration = (category, severity = 'emergency') => {
    if (!('vibrate' in navigator)) {
      console.log('Vibration API not supported on this device');
      return;
    }

    // EMERGENCY = JACKHAMMER. Long sustained pulses with tight 30 ms gaps
    // (perceptually continuous). Patterns mix long & short to defeat the
    // user's adaptation reflex. ~7-8 seconds per loop, repeated immediately.
    const emergencyPatterns = {
      threat:       [1500, 30, 800, 30, 1500, 30, 800, 30, 1500, 30, 600, 30, 1200, 30],
      medical:      [1200, 30, 1000, 30, 600, 30, 1200, 30, 1000, 30, 600, 30, 1200, 30],
      fire:         [1500, 30, 500, 30, 1500, 30, 500, 30, 1500, 30, 500, 30, 1500, 30],
      disturbance:  [1200, 30, 1000, 30, 600, 30, 1200, 30, 1000, 30, 600, 30, 1200, 30],
      child_safety: [1500, 30, 800, 30, 1500, 30, 800, 30, 1500, 30, 600, 30, 1200, 30],
      general:      [1200, 30, 800, 30, 1200, 30, 800, 30, 1500, 30, 600, 30, 1200, 30],
    };

    // NON-EMERGENCY = strong but spaced. Still firm pulses so user can't miss
    // it, but with a longer rest between loops.
    const nonEmergencyPatterns = {
      threat:       [400, 80, 400, 80, 400, 80],
      medical:      [400, 80, 400, 80, 400, 80],
      fire:         [400, 80, 400, 80, 400, 80],
      disturbance:  [400, 80, 400, 80, 400, 80],
      child_safety: [400, 80, 400, 80, 400, 80],
      general:      [300, 100, 300, 100, 300, 100],
    };

    const patterns = severity === 'non_emergency' ? nonEmergencyPatterns : emergencyPatterns;
    const pattern = patterns[category] || patterns.general;
    const patternDuration = pattern.reduce((a, b) => a + b, 0);

    // Clear any previous loop marker before starting fresh
    vibrationIntervalRef.current = null;

    // Fire the first pulse immediately
    try {
      navigator.vibrate(pattern);
    } catch (e) {
      console.log('Vibration failed:', e);
    }

    // Loop with the tightest gap appropriate for the severity.
    const loopGap = severity === 'non_emergency' ? 5000 : 30; // 30 ms = perceptually continuous
    vibrationTimeoutRef.current = setTimeout(() => {
      if (vibrationIntervalRef.current !== 'stopped') {
        vibrationIntervalRef.current = setInterval(() => {
          if ('vibrate' in navigator && vibrationIntervalRef.current !== 'stopped') {
            try {
              navigator.vibrate(pattern);
            } catch (e) { console.debug('[overlay] vibrate blocked:', e?.message); }
          }
        }, patternDuration + loopGap);
      }
    }, patternDuration);
  };

  const stopVibration = () => {
    // Clear the timeout that starts the interval
    if (vibrationTimeoutRef.current) {
      clearTimeout(vibrationTimeoutRef.current);
      vibrationTimeoutRef.current = null;
    }
    
    // Mark as stopped first to prevent new intervals
    const intervalId = vibrationIntervalRef.current;
    vibrationIntervalRef.current = 'stopped';
    
    if (intervalId && intervalId !== 'stopped') {
      clearInterval(intervalId);
    }
    
    // Force stop vibration immediately - call multiple times
    if ('vibrate' in navigator) {
      navigator.vibrate(0);
      navigator.vibrate([]);
      setTimeout(() => {
        navigator.vibrate(0);
        navigator.vibrate([]);
      }, 50);
      setTimeout(() => {
        navigator.vibrate(0);
        navigator.vibrate([]);
      }, 150);
    }
  };

  const dismissAlert = (e) => {
    e?.stopPropagation();
    if (activeAlert) {
      setDismissed(prev => new Set([...prev, activeAlert.id]));
      stopVibration();
      stopAlertSound();
    }
  };

  // Acknowledge the active alert with an ETA
  const acknowledgeAlert = async () => {
    if (!activeAlert || acking) return;
    setAcking(true);
    try {
      const res = await axios.post(`${API_URL}/alerts/${activeAlert.id}/acknowledge`, { eta: selectedEta });
      // Optimistically refresh local activeAlert with server response so the
      // panel updates immediately without waiting for WebSocket round-trip
      if (res?.data) {
        setActiveAlert(prev => (prev && prev.id === res.data.id ? { ...prev, ...res.data } : prev));
      }
      const etaLabels = { '1m': '1 min', '3m': '3 min', '5m': '5 min', '10m': '10 min', 'on_site': 'ON SITE' };
      toast.success(`Acknowledged · ETA ${etaLabels[selectedEta] || selectedEta}`);
    } catch (err) {
      console.error('Acknowledge failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setAcking(false);
    }
  };

  const withdrawAcknowledgment = async () => {
    if (!activeAlert || acking) return;
    setAcking(true);
    try {
      const res = await axios.delete(`${API_URL}/alerts/${activeAlert.id}/acknowledge`);
      if (res?.data) {
        setActiveAlert(prev => (prev && prev.id === res.data.id ? { ...prev, ...res.data } : prev));
      }
      toast.info('Acknowledgment withdrawn');
    } catch (err) {
      console.error('Withdraw ack failed:', err);
      toast.error('Failed to withdraw');
    } finally {
      setAcking(false);
    }
  };

  // Quick status update - sends to emergency chat for team visibility
  const updateStatus = async (status) => {
    const statusLabels = {
      'responding': '🟢 RESPONDING',
      'en_route': '🟠 EN ROUTE',
      'standby': '🟡 ON STANDBY',
      'all_clear': '✅ ALL CLEAR'
    };
    
    const statusMessage = `[STATUS UPDATE] ${user?.name}: ${statusLabels[status] || status.toUpperCase()}`;
    
    // Add message to local state immediately for instant display
    const tempMessage = {
      id: `temp-${Date.now()}`,
      content: statusMessage,
      sender_id: user?.id,
      sender_name: user?.name,
      created_at: new Date().toISOString(),
      encrypted: false,
      chat_type: 'emergency'
    };
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      // Update user's status in backend
      await axios.post(`${API_URL}/status/update`, { status });
      
      // Send status update to emergency chat so team can see who is responding
      if (activeAlert) {
        await axios.post(`${API_URL}/messages`, {
          content: statusMessage,
          encrypted: false,
          chat_type: 'emergency'
        });
      }
      
      // Also broadcast via WebSocket for real-time visibility
      sendWsMessage({
        type: 'status_update',
        status: status,
        user_name: user?.name,
        user_id: user?.id,
        alert_id: activeAlert?.id,
        message: statusMessage
      });
      
      toast.success(`Status: ${statusLabels[status] || status.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };


  // Prevent scroll from propagating to background
  const handleOverlayScroll = (e) => {
    e.stopPropagation();
  };

  if (!activeAlert) return null;

  const config = ALERT_CONFIG[activeAlert.category] || ALERT_CONFIG.general;
  const Icon = config.icon;
  const timeSince = getTimeSince(activeAlert.created_at);
  
  // Use orange color scheme for non-emergency alerts
  const isNonEmergency = activeAlert.severity === 'non_emergency';
  const displayColor = isNonEmergency ? '#f97316' : config.color;
  const displayBgColor = isNonEmergency ? 'rgba(249, 115, 22, 0.25)' : config.bgColor;
  const displayLabel = isNonEmergency ? `${config.label} ISSUE` : `${config.label} ALERT`;

  return (
    <>
      {/* Full screen VIBRANT color overlay */}
      <div 
        className="fixed inset-0 z-[100] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, ${displayBgColor} 0%, ${displayBgColor} 40%, transparent 80%)`,
          animation: isNonEmergency ? 'alertPulseSlow 1.5s ease-in-out infinite' : 'alertPulse 0.8s ease-in-out infinite',
        }}
      />

      {/* Screen border pulse - THICKER and more visible */}
      <div 
        className="fixed inset-0 z-[100] pointer-events-none"
        style={{
          boxShadow: `inset 0 0 80px ${displayColor}, inset 0 0 150px ${displayColor}80, inset 0 0 250px ${displayColor}40`,
          animation: isNonEmergency ? 'borderPulseSlow 1.5s ease-in-out infinite' : 'borderPulse 0.8s ease-in-out infinite',
        }}
      />

      {/* Alert Banner - Mobile Optimized with LARGER borders */}
      <div className="fixed top-0 left-0 right-0 z-[101] p-2 sm:p-4 lg:pl-[17rem]">
        <div 
          className="max-w-4xl mx-auto rounded-sm overflow-hidden shadow-2xl"
          style={{ 
            backgroundColor: '#0a0a0a',
            border: `4px solid ${displayColor}`,  /* THICKER border */
            boxShadow: `0 0 40px ${displayColor}, 0 0 80px ${displayColor}60`,  /* More glow */
          }}
        >
          {/* Color bar at top - THICKER */}
          <div 
            className="h-2 w-full"  /* Thicker bar */
            style={{ 
              backgroundColor: displayColor,
              animation: isNonEmergency ? 'colorBarPulseSlow 1s ease-in-out infinite' : 'colorBarPulse 0.5s ease-in-out infinite',
            }}
          />
          
          <div className="p-3 sm:p-4">
            {/* Mobile Layout */}
            <div className="flex flex-col gap-3">
              {/* Header Row */}
              <div className="flex items-center gap-3">
                {/* Icon - with glow effect */}
                <div 
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-sm flex items-center justify-center shrink-0"
                  style={{ 
                    backgroundColor: `${displayColor}30`,
                    boxShadow: `0 0 20px ${displayColor}60`,
                    animation: isNonEmergency ? 'iconPulseSlow 1.5s ease-in-out infinite' : 'iconPulse 0.8s ease-in-out infinite',
                  }}
                >
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: displayColor }} />
                </div>

                {/* Title & Time */}
                <div className="flex-1 min-w-0">
                  <span 
                    className="font-chivo font-black text-xl sm:text-2xl uppercase tracking-wider block"
                    style={{ 
                      color: displayColor,
                      textShadow: `0 0 10px ${displayColor}80`,
                      animation: isNonEmergency ? 'textPulseSlow 1.5s ease-in-out infinite' : 'textPulse 0.8s ease-in-out infinite',
                    }}
                  >
                    {displayLabel}
                  </span>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span className="font-mono text-xs">{timeSince}</span>
                  </div>
                </div>

                {/* Dismiss Button */}
                <div className="flex gap-1 shrink-0">
                  {isIOS && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleAudioMute}
                      className={`h-8 w-8 hover:bg-white/10 rounded-sm ${audioMuted ? 'text-alert-red' : 'text-green-400'}`}
                      title={audioMuted ? 'Unmute siren (iPhone audio fallback)' : 'Mute siren'}
                      data-testid="overlay-audio-mute-btn"
                    >
                      {audioMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={dismissAlert}
                    className="h-8 w-8 hover:bg-white/10 rounded-sm"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Location - Prominent */}
              <div 
                className="flex items-center gap-2 p-2 rounded-sm"
                style={{ backgroundColor: `${displayColor}15` }}
              >
                <MapPin className="w-5 h-5" style={{ color: displayColor }} />
                <span 
                  className="font-mono text-base font-bold uppercase"
                  style={{ color: config.color }}
                >
                  {activeAlert.location}
                </span>
              </div>

              {/* Description */}
              <p className="font-inter text-white text-sm sm:text-base">
                {activeAlert.description || 'Emergency alert triggered'}
              </p>


              {/* Quick Status Update Buttons (MOVED DOWN) */}
              <div className="grid grid-cols-4 gap-2">
                <Button
                  onClick={() => updateStatus('responding')}
                  className="h-10 font-mono text-[10px] uppercase rounded-sm bg-blue-600 hover:bg-blue-700 text-white flex flex-col items-center justify-center gap-0.5 p-1"
                >
                  <UserCheck className="w-4 h-4" />
                  <span>Responding</span>
                </Button>
                <Button
                  onClick={() => updateStatus('en_route')}
                  className="h-10 font-mono text-[10px] uppercase rounded-sm bg-orange-600 hover:bg-orange-700 text-white flex flex-col items-center justify-center gap-0.5 p-1"
                >
                  <Navigation className="w-4 h-4" />
                  <span>Enroute</span>
                </Button>
                <Button
                  onClick={() => updateStatus('standby')}
                  className="h-10 font-mono text-[10px] uppercase rounded-sm bg-yellow-600 hover:bg-yellow-700 text-white flex flex-col items-center justify-center gap-0.5 p-1"
                >
                  <Shield className="w-4 h-4" />
                  <span>Standby</span>
                </Button>
                <Button
                  onClick={() => updateStatus('all_clear')}
                  className="h-10 font-mono text-[10px] uppercase rounded-sm bg-green-600 hover:bg-green-700 text-white flex flex-col items-center justify-center gap-0.5 p-1"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Clear</span>
                </Button>
              </div>

              {/* ACKNOWLEDGE / ETA PANEL */}
              <AcknowledgePanel
                alert={activeAlert}
                userId={user?.id}
                displayColor={displayColor}
                selectedEta={selectedEta}
                onSelectEta={setSelectedEta}
                acking={acking}
                onAcknowledge={acknowledgeAlert}
                onWithdraw={withdrawAcknowledgment}
              />

              {/* EMERGENCY CHAT */}
              <EmergencyChat
                messages={messages}
                user={user}
                chatExpanded={chatExpanded}
                onToggleExpanded={() => setChatExpanded(!chatExpanded)}
                chatScrollRef={chatScrollRef}
                chatEndRef={chatEndRef}
                chatInputRef={chatInputRef}
                handleOverlayScroll={handleOverlayScroll}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                isRecording={isRecording}
                startVoiceRecording={startVoiceRecording}
                stopVoiceRecording={stopVoiceRecording}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes alertPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.9; }
        }
        
        @keyframes alertPulseSlow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        
        @keyframes borderPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        
        @keyframes borderPulseSlow {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
        
        @keyframes colorBarPulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        
        @keyframes colorBarPulseSlow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.9; }
        }
        
        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes iconPulseSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes chatPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(220, 38, 38, 0.3); }
          50% { box-shadow: 0 0 30px rgba(220, 38, 38, 0.5); }
        }
        
        @keyframes textPulse {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 1; }
        }
        
        @keyframes textPulseSlow {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        
        @keyframes resolveFlash {
          0%, 100% { 
            background-color: rgba(34, 197, 94, 0.1);
            border-color: rgba(34, 197, 94, 0.5);
            box-shadow: 0 0 8px rgba(34, 197, 94, 0.2);
          }
          50% { 
            background-color: rgba(34, 197, 94, 0.35);
            border-color: rgba(34, 197, 94, 1);
            box-shadow: 0 0 20px rgba(34, 197, 94, 0.5);
          }
        }
      `}</style>
    </>
  );
};

function getTimeSince(dateStr) {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${diffHour}h ago`;
}

export default GlobalAlertOverlay;
